// @ts-check
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../api.js';

const TABS = /** @type {const} */ ([
  { key: 'products', label: 'Products' },
  { key: 'brands', label: 'Brands' },
  { key: 'categories', label: 'Categories' },
  { key: 'stock', label: 'Stock' },
  { key: 'purchases', label: 'Purchases' },
  { key: 'customers', label: 'Customers' },
  { key: 'vendors', label: 'Vendors' },
  { key: 'users', label: 'Users' },
  { key: 'settings', label: 'Branding & Printer' },
]);

export default function BranchDetail() {
  const { id } = useParams();
  const branchId = /** @type {string} */ (id);
  const [tab, setTab] = useState(/** @type {typeof TABS[number]['key']} */ ('products'));
  const [branch, setBranch] = useState(/** @type {any} */ (null));
  const [err, setErr] = useState('');

  useEffect(() => {
    api('/admin/branches')
      .then((r) => setBranch(r.branches.find(/** @param {any} b */ (b) => b._id === branchId) ?? null))
      .catch((e) => setErr(e.message));
  }, [branchId]);

  return (
    <div className="space-y-4">
      <div className="flex items-baseline gap-3">
        <Link to="/branches" className="text-neutral-400 hover:underline">← Branches</Link>
        <h1 className="text-3xl font-bold">{branch?.name ?? branchId}</h1>
        {branch && <span className="text-neutral-500 font-mono">{branch.code}</span>}
      </div>
      {err && <p className="text-red-400">{err}</p>}

      <div className="flex gap-2 border-b border-neutral-800">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm ${tab === t.key ? 'text-red-400 border-b-2 border-red-400 -mb-px' : 'text-neutral-400 hover:text-neutral-200'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'products' && <ProductsTab branchId={branchId} />}
      {tab === 'brands' && <BrandsTab branchId={branchId} />}
      {tab === 'categories' && <CategoriesTab branchId={branchId} />}
      {tab === 'stock' && <StockTab branchId={branchId} />}
      {tab === 'purchases' && <PurchasesTab branchId={branchId} />}
      {tab === 'customers' && <CustomersTab branchId={branchId} />}
      {tab === 'vendors' && <VendorsTab branchId={branchId} />}
      {tab === 'users' && <UsersTab branchId={branchId} />}
      {tab === 'settings' && <BrandingPrinterTab branchId={branchId} branch={branch} onUpdated={() => { /* parent refresh hook reserved */ }} />}
    </div>
  );
}

/**
 * HQ-side branding + printer config. Pushes logo, GSTIN, footer, and the
 * Retsol VID/PID hex values into the branch row via the generic upsert
 * endpoint so they sync down to the desktop where the printer service reads
 * them.
 *
 * @param {{ branchId: string, branch: any }} props
 */
function BrandingPrinterTab({ branchId, branch }) {
  const [form, setForm] = useState({
    name: '', code: '', gstin: '', state_code: '', address: '', phone: '', email: '',
    logo_b64: '', printer_vendor_id: '', printer_product_id: '', receipt_footer: '',
    enrolment_secret: '',
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!branch) return;
    setForm((prev) => ({
      ...prev,
      name: branch.name ?? '', code: branch.code ?? '', gstin: branch.gstin ?? '',
      state_code: branch.state_code ?? '', address: branch.address ?? '',
      phone: branch.phone ?? '', email: branch.email ?? '',
      logo_b64: branch.logo_b64 ?? '',
      printer_vendor_id: branch.printer_vendor_id ?? '',
      printer_product_id: branch.printer_product_id ?? '',
      receipt_footer: branch.receipt_footer ?? '',
      enrolment_secret: branch.enrolment_secret ?? '',
    }));
  }, [branch]);

  function uploadLogo(/** @type {React.ChangeEvent<HTMLInputElement>} */ e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm({ ...form, logo_b64: /** @type {string} */ (reader.result) });
    reader.readAsDataURL(file);
  }

  async function save(/** @type {React.FormEvent} */ e) {
    e.preventDefault();
    setBusy(true); setErr(''); setMsg('');
    try {
      // POST /admin/branches is the SUPER_ADMIN-only branch upsert; it needs
      // a valid enrolment_secret (we surface the existing value so admins can
      // tweak surrounding fields without rotating the secret).
      await api('/admin/branches', {
        method: 'POST',
        body: JSON.stringify({
          id: branchId,
          name: form.name,
          code: form.code,
          gstin: form.gstin || undefined,
          state_code: form.state_code || undefined,
          address: form.address || undefined,
          phone: form.phone || undefined,
          email: form.email || undefined,
          enrolment_secret: form.enrolment_secret,
        }),
      });
      // Extended fields (logo, printer ids, footer) go via the generic upsert
      // so the existing /admin/branches schema doesn't have to grow every time
      // we add a new branding column.
      await api(`/admin/branches/${branchId}/upsert`, {
        method: 'POST',
        body: JSON.stringify({
          table: 'branches',
          doc: {
            id: branchId,
            logo_b64: form.logo_b64 || undefined,
            printer_vendor_id: form.printer_vendor_id || undefined,
            printer_product_id: form.printer_product_id || undefined,
            receipt_footer: form.receipt_footer || undefined,
          },
        }),
      });
      setMsg('Saved — desktop receives on next sync tick.');
    } catch (e) { setErr(/** @type {Error} */ (e).message); }
    finally { setBusy(false); }
  }

  return (
    <form onSubmit={save} className="card max-w-3xl space-y-4">
      <h2 className="font-semibold">Identity</h2>
      <div className="grid grid-cols-2 gap-3">
        <TextInput label="Branch name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
        <TextInput label="Code" value={form.code} onChange={(v) => setForm({ ...form, code: v })} required />
        <TextInput label="GSTIN" value={form.gstin} onChange={(v) => setForm({ ...form, gstin: v })} />
        <TextInput label="State code" value={form.state_code} onChange={(v) => setForm({ ...form, state_code: v })} />
        <TextInput label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
        <TextInput label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
      </div>
      <TextInput label="Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} />
      <TextInput label="Receipt footer" value={form.receipt_footer} onChange={(v) => setForm({ ...form, receipt_footer: v })} />

      <h2 className="font-semibold pt-3">Logo (printed on every bill)</h2>
      <div className="flex items-center gap-3">
        {form.logo_b64 && <img src={form.logo_b64} alt="logo" className="h-16 bg-white p-1 rounded" />}
        <input type="file" accept="image/png,image/jpeg" onChange={uploadLogo} />
        {form.logo_b64 && <button type="button" onClick={() => setForm({ ...form, logo_b64: '' })} className="btn-secondary text-xs">Remove</button>}
      </div>

      <h2 className="font-semibold pt-3">Retsol RTP-82UE printer</h2>
      <div className="grid grid-cols-2 gap-3">
        <TextInput label="USB vendor id (hex)" value={form.printer_vendor_id} onChange={(v) => setForm({ ...form, printer_vendor_id: v })} />
        <TextInput label="USB product id (hex)" value={form.printer_product_id} onChange={(v) => setForm({ ...form, printer_product_id: v })} />
      </div>
      <p className="text-xs text-neutral-500">Leave blank to auto-detect first ESC/POS USB device on the branch machine.</p>

      <TextInput label="Enrolment secret (existing)" value={form.enrolment_secret} onChange={(v) => setForm({ ...form, enrolment_secret: v })} />

      {err && <p className="text-red-400 text-sm">{err}</p>}
      {msg && <p className="text-green-400 text-sm">{msg}</p>}
      <button disabled={busy} className="btn-primary">{busy ? 'Saving…' : 'Save settings'}</button>
    </form>
  );
}

/** @param {{ branchId: string }} props */
function ProductsTab({ branchId }) {
  const [rows, setRows] = useState(/** @type {any[]} */ ([]));
  const [brands, setBrands] = useState(/** @type {any[]} */ ([]));
  const [categories, setCategories] = useState(/** @type {any[]} */ ([]));
  const [sortKey, setSortKey] = useState(/** @type {'brand' | 'name' | 'sku'} */ ('brand'));
  const [filterBrand, setFilterBrand] = useState('');
  const [form, setForm] = useState({ sku: '', name: '', mrp: '', sale: '', hsn: '', gst: '18', stock_uom: 'PCS', purchase_uom: 'PCS', avg_weight: '', pricing_mode: 'MRP', brand_id: '', category_id: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  async function load() {
    const [p, b, c] = await Promise.all([
      api(`/admin/branches/${branchId}/data/products?limit=500`),
      api(`/admin/branches/${branchId}/data/brands?limit=500`).catch(() => ({ rows: [] })),
      api(`/admin/branches/${branchId}/data/categories?limit=500`).catch(() => ({ rows: [] })),
    ]);
    setRows(p.rows);
    setBrands(b.rows);
    setCategories(c.rows);
  }
  useEffect(() => { load().catch((e) => setErr(e.message)); }, [branchId]);

  async function submit(/** @type {React.FormEvent} */ e) {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      // MRP mode requires mrp_paise; LOOSE leaves it null and uses sale price only.
      // DUOM = purchase_uom different from stock_uom (e.g. buy by KG, sell by PCS).
      const isLoose = form.pricing_mode === 'LOOSE';
      const isDuom = form.purchase_uom !== form.stock_uom;
      const doc = {
        sku: form.sku,
        name: form.name,
        sale_price_paise: rupeesToPaise(form.sale),
        hsn: form.hsn || undefined,
        gst_rate: Number(form.gst) || 0,
        stock_uom: form.stock_uom,
        purchase_uom: form.purchase_uom,
        pricing_mode: form.pricing_mode,
        brand_id: form.brand_id || undefined,
        category_id: form.category_id || undefined,
        active: true,
      };
      if (!isLoose) doc.mrp_paise = rupeesToPaise(form.mrp);
      if (isDuom) doc.avg_weight_per_piece = Number(form.avg_weight) || undefined;
      await api(`/admin/branches/${branchId}/upsert`, {
        method: 'POST',
        body: JSON.stringify({ table: 'products', doc }),
      });
      setForm({ sku: '', name: '', mrp: '', sale: '', hsn: '', gst: '18', stock_uom: 'PCS', purchase_uom: 'PCS', avg_weight: '', pricing_mode: 'MRP', brand_id: '', category_id: '' });
      await load();
    } catch (e) {
      setErr(/** @type {Error} */ (e).message);
    } finally { setBusy(false); }
  }

  /** @type {Record<string, string>} */
  const brandNameById = Object.fromEntries(brands.map((b) => [b.id, b.name]));
  const visible = rows
    .filter((p) => !filterBrand || p.brand_id === filterBrand)
    .slice()
    .sort((a, b) => {
      if (sortKey === 'brand') {
        return (brandNameById[a.brand_id] ?? 'zzz').localeCompare(brandNameById[b.brand_id] ?? 'zzz')
          || String(a.name).localeCompare(String(b.name));
      }
      if (sortKey === 'sku') return String(a.sku).localeCompare(String(b.sku));
      return String(a.name).localeCompare(String(b.name));
    });

  return (
    <div className="grid grid-cols-3 gap-4">
      <form onSubmit={submit} className="card col-span-1 space-y-3 self-start">
        <h2 className="font-semibold">Add product</h2>
        <TextInput label="SKU" value={form.sku} onChange={(v) => setForm({ ...form, sku: v })} required />
        <TextInput label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
        <SelectInput
          label="Brand"
          value={form.brand_id}
          onChange={(v) => setForm({ ...form, brand_id: v })}
          options={[{ value: '', label: '— none —' }, ...brands.map((b) => ({ value: b.id, label: b.name }))]}
        />
        <SelectInput
          label="Category"
          value={form.category_id}
          onChange={(v) => setForm({ ...form, category_id: v })}
          options={[{ value: '', label: '— none —' }, ...categories.map((c) => ({ value: c.id, label: c.name }))]}
        />
        <SelectInput
          label="Pricing mode"
          value={form.pricing_mode}
          onChange={(v) => setForm({ ...form, pricing_mode: v, mrp: v === 'LOOSE' ? '' : form.mrp })}
          options={[
            { value: 'MRP', label: 'MRP (tax-inclusive)' },
            { value: 'LOOSE', label: 'LOOSE (entry price)' },
          ]}
        />
        {form.pricing_mode === 'MRP' && (
          <TextInput label="MRP (₹)" value={form.mrp} onChange={(v) => setForm({ ...form, mrp: v })} required />
        )}
        <TextInput label="Sale price (₹)" value={form.sale} onChange={(v) => setForm({ ...form, sale: v })} required />
        <TextInput label="HSN code" value={form.hsn} onChange={(v) => setForm({ ...form, hsn: v })} />
        <TextInput label="GST rate (%)" value={form.gst} onChange={(v) => setForm({ ...form, gst: v })} />
        <div className="grid grid-cols-2 gap-2">
          <SelectInput
            label="Stock UOM"
            value={form.stock_uom}
            onChange={(v) => setForm({ ...form, stock_uom: v })}
            options={['PCS', 'KG', 'G', 'L'].map((x) => ({ value: x, label: x }))}
          />
          <SelectInput
            label="Purchase UOM"
            value={form.purchase_uom}
            onChange={(v) => setForm({ ...form, purchase_uom: v })}
            options={['PCS', 'KG', 'G', 'L'].map((x) => ({ value: x, label: x }))}
          />
        </div>
        {form.purchase_uom !== form.stock_uom && (
          <TextInput label="Avg weight per piece (DUOM)" value={form.avg_weight} onChange={(v) => setForm({ ...form, avg_weight: v })} />
        )}
        {err && <p className="text-red-400 text-sm">{err}</p>}
        <button disabled={busy} className="btn-primary w-full">{busy ? 'Saving…' : 'Create'}</button>
      </form>
      <div className="card col-span-2 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-semibold">Products ({visible.length}{filterBrand && ` of ${rows.length}`})</h2>
          <div className="flex gap-2 text-sm">
            <select className="input" value={sortKey} onChange={(e) => setSortKey(/** @type {any} */ (e.target.value))}>
              <option value="brand">Sort: Brand</option>
              <option value="name">Sort: Name</option>
              <option value="sku">Sort: SKU</option>
            </select>
            <select className="input" value={filterBrand} onChange={(e) => setFilterBrand(e.target.value)}>
              <option value="">All brands</option>
              {brands.map((b) => <option key={b._id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="text-neutral-400 text-left border-b border-neutral-800">
            <tr><th className="py-2">SKU</th><th>Name</th><th>Brand</th><th>MRP</th><th>Sale</th><th>GST</th></tr>
          </thead>
          <tbody>
            {visible.map((p) => (
              <tr key={p._id} className="border-b border-neutral-900">
                <td className="py-1 font-mono">{p.sku}</td>
                <td>{p.name}</td>
                <td>{brandNameById[p.brand_id] ?? '—'}</td>
                <td>{fmtMoney(p.mrp_paise)}</td>
                <td>{fmtMoney(p.sale_price_paise)}</td>
                <td>{p.gst_rate ?? '—'}%</td>
              </tr>
            ))}
            {visible.length === 0 && <tr><td colSpan={6} className="py-3 text-neutral-500">No products{filterBrand && ' for this brand'}.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** @param {{ branchId: string }} props */
function BrandsTab({ branchId }) {
  const [brands, setBrands] = useState(/** @type {any[]} */ ([]));
  const [rules, setRules] = useState(/** @type {any[]} */ ([]));
  const [form, setForm] = useState({ name: '', slug: '', logo_url: '' });
  const [ruleForm, setRuleForm] = useState({ brand_id: '', max_pct: '', floor_pct: '', min_price: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  async function load() {
    const [b, r] = await Promise.all([
      api(`/admin/branches/${branchId}/data/brands?limit=500`).catch(() => ({ rows: [] })),
      api(`/admin/branches/${branchId}/data/brand_discount_rules?limit=500`).catch(() => ({ rows: [] })),
    ]);
    setBrands(b.rows); setRules(r.rows);
  }
  useEffect(() => { load().catch((e) => setErr(e.message)); }, [branchId]);

  async function submitBrand(/** @type {React.FormEvent} */ e) {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      await api(`/admin/branches/${branchId}/upsert`, {
        method: 'POST',
        body: JSON.stringify({ table: 'brands', doc: { name: form.name, slug: form.slug || undefined, logo_url: form.logo_url || undefined, active: true } }),
      });
      setForm({ name: '', slug: '', logo_url: '' });
      await load();
    } catch (e) { setErr(/** @type {Error} */ (e).message); }
    finally { setBusy(false); }
  }

  async function submitRule(/** @type {React.FormEvent} */ e) {
    e.preventDefault();
    if (!ruleForm.brand_id) { setErr('pick a brand'); return; }
    setBusy(true); setErr('');
    try {
      // bps: percentage * 100, so 12.5% -> 1250 bps. Stored as integer for precision.
      const maxBps = Math.round((Number(ruleForm.max_pct) || 0) * 100);
      const floorBps = Math.round((Number(ruleForm.floor_pct) || 0) * 100);
      const minPaise = rupeesToPaise(ruleForm.min_price);
      // Re-use existing rule id if one already exists for this brand (one-rule-per-brand).
      const existing = rules.find((r) => r.brand_id === ruleForm.brand_id);
      await api(`/admin/branches/${branchId}/upsert`, {
        method: 'POST',
        body: JSON.stringify({
          table: 'brand_discount_rules',
          doc: {
            ...(existing ? { id: existing.id } : {}),
            brand_id: ruleForm.brand_id,
            max_discount_bps: maxBps,
            floor_pct_of_mrp_bps: floorBps,
            min_sale_price_paise: minPaise,
            active: true,
          },
        }),
      });
      setRuleForm({ brand_id: '', max_pct: '', floor_pct: '', min_price: '' });
      await load();
    } catch (e) { setErr(/** @type {Error} */ (e).message); }
    finally { setBusy(false); }
  }

  /** @type {Record<string, any>} */
  const brandById = Object.fromEntries(brands.map((b) => [b.id, b]));

  return (
    <div className="grid grid-cols-2 gap-4">
      <form onSubmit={submitBrand} className="card space-y-3 self-start">
        <h2 className="font-semibold">Add brand</h2>
        <TextInput label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
        <TextInput label="Slug" value={form.slug} onChange={(v) => setForm({ ...form, slug: v })} />
        <TextInput label="Logo URL" value={form.logo_url} onChange={(v) => setForm({ ...form, logo_url: v })} />
        {err && <p className="text-red-400 text-sm">{err}</p>}
        <button disabled={busy} className="btn-primary w-full">{busy ? 'Saving…' : 'Create brand'}</button>
      </form>

      <form onSubmit={submitRule} className="card space-y-3 self-start">
        <h2 className="font-semibold">Brand discount cap</h2>
        <SelectInput
          label="Brand"
          value={ruleForm.brand_id}
          onChange={(v) => setRuleForm({ ...ruleForm, brand_id: v })}
          options={[{ value: '', label: 'pick a brand…' }, ...brands.map((b) => ({ value: b.id, label: b.name }))]}
        />
        <TextInput label="Max discount %" value={ruleForm.max_pct} onChange={(v) => setRuleForm({ ...ruleForm, max_pct: v })} />
        <TextInput label="Floor % of MRP" value={ruleForm.floor_pct} onChange={(v) => setRuleForm({ ...ruleForm, floor_pct: v })} />
        <TextInput label="Absolute min sale price (₹)" value={ruleForm.min_price} onChange={(v) => setRuleForm({ ...ruleForm, min_price: v })} />
        <p className="text-xs text-neutral-500">POS billing rejects any line where discount % exceeds the cap, or per-unit price drops below either floor.</p>
        <button disabled={busy} className="btn-primary w-full">{busy ? 'Saving…' : 'Save rule'}</button>
      </form>

      <div className="card col-span-2">
        <h2 className="font-semibold mb-2">Brands ({brands.length})</h2>
        <table className="w-full text-sm">
          <thead className="text-neutral-400 text-left border-b border-neutral-800">
            <tr><th className="py-2">Name</th><th>Slug</th><th>Max disc %</th><th>Floor % of MRP</th><th>Min sale ₹</th></tr>
          </thead>
          <tbody>
            {brands.map((b) => {
              const r = rules.find((x) => x.brand_id === b.id);
              return (
                <tr key={b._id} className="border-b border-neutral-900">
                  <td className="py-1">{b.name}</td>
                  <td className="text-neutral-500">{b.slug ?? '—'}</td>
                  <td>{r ? (Number(r.max_discount_bps) / 100).toFixed(2) : '—'}</td>
                  <td>{r ? (Number(r.floor_pct_of_mrp_bps) / 100).toFixed(2) : '—'}</td>
                  <td>{r && r.min_sale_price_paise ? fmtMoney(r.min_sale_price_paise) : '—'}</td>
                </tr>
              );
            })}
            {brands.length === 0 && <tr><td colSpan={5} className="py-3 text-neutral-500">No brands yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** @param {{ branchId: string }} props */
function CategoriesTab({ branchId }) {
  const [rows, setRows] = useState(/** @type {any[]} */ ([]));
  const [form, setForm] = useState({ name: '', parent_id: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  async function load() {
    const r = await api(`/admin/branches/${branchId}/data/categories?limit=500`).catch(() => ({ rows: [] }));
    setRows(r.rows);
  }
  useEffect(() => { load().catch((e) => setErr(e.message)); }, [branchId]);

  async function submit(/** @type {React.FormEvent} */ e) {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      await api(`/admin/branches/${branchId}/upsert`, {
        method: 'POST',
        body: JSON.stringify({ table: 'categories', doc: { name: form.name, parent_id: form.parent_id || undefined } }),
      });
      setForm({ name: '', parent_id: '' });
      await load();
    } catch (e) { setErr(/** @type {Error} */ (e).message); }
    finally { setBusy(false); }
  }

  return (
    <div className="grid grid-cols-3 gap-4">
      <form onSubmit={submit} className="card col-span-1 space-y-3 self-start">
        <h2 className="font-semibold">Add category</h2>
        <TextInput label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
        <SelectInput
          label="Parent"
          value={form.parent_id}
          onChange={(v) => setForm({ ...form, parent_id: v })}
          options={[{ value: '', label: '— top level —' }, ...rows.map((c) => ({ value: c.id, label: c.name }))]}
        />
        {err && <p className="text-red-400 text-sm">{err}</p>}
        <button disabled={busy} className="btn-primary w-full">{busy ? 'Saving…' : 'Create'}</button>
      </form>
      <div className="card col-span-2">
        <h2 className="font-semibold mb-2">Categories ({rows.length})</h2>
        <table className="w-full text-sm">
          <thead className="text-neutral-400 text-left border-b border-neutral-800">
            <tr><th className="py-2">Name</th><th>Parent</th></tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c._id} className="border-b border-neutral-900">
                <td className="py-1">{c.name}</td>
                <td className="text-neutral-500">{rows.find((p) => p.id === c.parent_id)?.name ?? '—'}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={2} className="py-3 text-neutral-500">No categories yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** @param {{ branchId: string }} props */
function StockTab({ branchId }) {
  const [rows, setRows] = useState(/** @type {any[]} */ ([]));
  const [products, setProducts] = useState(/** @type {any[]} */ ([]));
  const [form, setForm] = useState({ product_id: '', qty_delta: '', reason: '', unit_cost: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  async function load() {
    const [s, p] = await Promise.all([
      api(`/admin/branches/${branchId}/data/stock?limit=500`).catch(() => ({ rows: [] })),
      api(`/admin/branches/${branchId}/data/products?limit=500`),
    ]);
    setRows(s.rows); setProducts(p.rows);
  }
  useEffect(() => { load().catch((e) => setErr(e.message)); }, [branchId]);

  async function submit(/** @type {React.FormEvent} */ e) {
    e.preventDefault();
    setBusy(true); setErr(''); setMsg('');
    try {
      const res = await api(`/admin/branches/${branchId}/stock/adjust`, {
        method: 'POST',
        body: JSON.stringify({
          product_id: form.product_id,
          qty_delta: Number(form.qty_delta),
          reason: form.reason,
          unit_cost_paise: form.unit_cost ? rupeesToPaise(form.unit_cost) : undefined,
        }),
      });
      setMsg(`new qty: ${res.qty_on_hand}`);
      setForm({ product_id: '', qty_delta: '', reason: '', unit_cost: '' });
      await load();
    } catch (e) { setErr(/** @type {Error} */ (e).message); }
    finally { setBusy(false); }
  }

  /** @type {Record<string, any>} */
  const productById = Object.fromEntries(products.map((p) => [p.id, p]));

  return (
    <div className="grid grid-cols-3 gap-4">
      <form onSubmit={submit} className="card col-span-1 space-y-3 self-start">
        <h2 className="font-semibold">Adjust stock</h2>
        <SelectInput
          label="Product"
          value={form.product_id}
          onChange={(v) => setForm({ ...form, product_id: v })}
          options={[{ value: '', label: 'pick a product…' }, ...products.map((p) => ({ value: p.id, label: `${p.sku} — ${p.name}` }))]}
        />
        <TextInput label="Quantity delta (signed)" value={form.qty_delta} onChange={(v) => setForm({ ...form, qty_delta: v })} required />
        <TextInput label="Reason" value={form.reason} onChange={(v) => setForm({ ...form, reason: v })} required />
        <TextInput label="Unit cost (₹, optional)" value={form.unit_cost} onChange={(v) => setForm({ ...form, unit_cost: v })} />
        {err && <p className="text-red-400 text-sm">{err}</p>}
        {msg && <p className="text-green-400 text-sm">{msg}</p>}
        <button disabled={busy} className="btn-primary w-full">{busy ? 'Saving…' : 'Apply adjustment'}</button>
      </form>
      <div className="card col-span-2">
        <h2 className="font-semibold mb-2">Stock ({rows.length})</h2>
        <table className="w-full text-sm">
          <thead className="text-neutral-400 text-left border-b border-neutral-800">
            <tr><th className="py-2">SKU</th><th>Product</th><th>Qty on hand</th><th>Updated</th></tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s._id} className="border-b border-neutral-900">
                <td className="py-1 font-mono">{productById[s.product_id]?.sku ?? '—'}</td>
                <td>{productById[s.product_id]?.name ?? s.product_id}</td>
                <td>{s.qty_on_hand ?? 0}</td>
                <td className="text-neutral-500">{fmtCell(s.updated_at)}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={4} className="py-3 text-neutral-500">No stock entries yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** @param {{ branchId: string }} props */
function CustomersTab({ branchId }) {
  return (
    <CrudTab
      branchId={branchId}
      table="customers"
      title="customer"
      fields={[
        { key: 'code', label: 'Code', required: true },
        { key: 'name', label: 'Name', required: true },
        { key: 'phone', label: 'Phone' },
        { key: 'email', label: 'Email' },
        { key: 'gstin', label: 'GSTIN' },
        { key: 'state_code', label: 'State code' },
      ]}
      columns={['code', 'name', 'phone', 'gstin']}
    />
  );
}

/** @param {{ branchId: string }} props */
function VendorsTab({ branchId }) {
  return (
    <CrudTab
      branchId={branchId}
      table="vendors"
      title="vendor"
      fields={[
        { key: 'vendor_code', label: 'Code', required: true },
        { key: 'name', label: 'Name', required: true },
        { key: 'phone', label: 'Phone' },
        { key: 'email', label: 'Email' },
        { key: 'gstin', label: 'GSTIN' },
        { key: 'state_code', label: 'State code' },
        { key: 'address', label: 'Address' },
      ]}
      columns={['vendor_code', 'name', 'phone', 'gstin']}
    />
  );
}

/** @param {{ branchId: string }} props */
function UsersTab({ branchId }) {
  const [rows, setRows] = useState(/** @type {any[]} */ ([]));
  const [form, setForm] = useState({ email: '', name: '', role: 'CASHIER', password: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  async function load() {
    const r = await api(`/admin/users`);
    setRows(r.users.filter(/** @param {any} u */ (u) => !branchId || u.branch_id === branchId || u.branch_id == null));
  }
  useEffect(() => { load().catch((e) => setErr(e.message)); }, [branchId]);

  async function submit(/** @type {React.FormEvent} */ e) {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      await api(`/admin/branches/${branchId}/users`, {
        method: 'POST',
        body: JSON.stringify(form),
      });
      setForm({ email: '', name: '', role: 'CASHIER', password: '' });
      await load();
    } catch (e) { setErr(/** @type {Error} */ (e).message); }
    finally { setBusy(false); }
  }

  const roleOptions = ['SUPER_ADMIN', 'BRANCH_MANAGER', 'CASHIER', 'ACCOUNTANT', 'STOCK_MANAGER', 'PURCHASE_OFFICER'].map((r) => ({ value: r, label: r }));

  return (
    <div className="grid grid-cols-3 gap-4">
      <form onSubmit={submit} className="card col-span-1 space-y-3 self-start">
        <h2 className="font-semibold">Create user</h2>
        <TextInput label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />
        <TextInput label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
        <SelectInput label="Role" value={form.role} onChange={(v) => setForm({ ...form, role: v })} options={roleOptions} />
        <TextInput label="Password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} required />
        {err && <p className="text-red-400 text-sm">{err}</p>}
        <button disabled={busy} className="btn-primary w-full">{busy ? 'Saving…' : 'Create'}</button>
      </form>
      <div className="card col-span-2">
        <h2 className="font-semibold mb-2">Users ({rows.length})</h2>
        <table className="w-full text-sm">
          <thead className="text-neutral-400 text-left border-b border-neutral-800">
            <tr><th className="py-2">Email</th><th>Name</th><th>Role</th><th>Branch</th></tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u._id} className="border-b border-neutral-900">
                <td className="py-1">{u.email}</td>
                <td>{u.name}</td>
                <td className="font-mono">{u.role}</td>
                <td className="text-neutral-500">{u.branch_id ?? '—'}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={4} className="py-3 text-neutral-500">No users.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** @param {{ branchId: string }} props */
function PurchasesTab({ branchId }) {
  const [rows, setRows] = useState(/** @type {any[]} */ ([]));
  const [vendors, setVendors] = useState(/** @type {any[]} */ ([]));
  const [products, setProducts] = useState(/** @type {any[]} */ ([]));
  const [header, setHeader] = useState({ vendor_id: '', mode: 'MRP', seller_state: '27', vendor_state: '27', invoice_no: '' });
  const [lines, setLines] = useState(/** @type {{ product_id: string, qty: string, rate: string }[]} */ ([{ product_id: '', qty: '', rate: '' }]));
  const [csv, setCsv] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  async function load() {
    const [p, v, pr] = await Promise.all([
      api(`/admin/branches/${branchId}/data/purchases?limit=100`).catch(() => ({ rows: [] })),
      api(`/admin/branches/${branchId}/data/vendors?limit=500`).catch(() => ({ rows: [] })),
      api(`/admin/branches/${branchId}/data/products?limit=500`).catch(() => ({ rows: [] })),
    ]);
    setRows(p.rows); setVendors(v.rows); setProducts(pr.rows);
  }
  useEffect(() => { load().catch((e) => setErr(e.message)); }, [branchId]);

  async function submitManual(/** @type {React.FormEvent} */ e) {
    e.preventDefault();
    setBusy(true); setErr(''); setMsg('');
    try {
      const res = await api(`/admin/branches/${branchId}/purchases`, {
        method: 'POST',
        body: JSON.stringify({
          vendor_id: header.vendor_id,
          mode: header.mode,
          seller_state: header.seller_state,
          vendor_state: header.vendor_state,
          invoice_no: header.invoice_no || undefined,
          lines: lines.filter((l) => l.product_id && l.qty && l.rate).map((l) => ({
            product_id: l.product_id,
            qty_purchased: Number(l.qty),
            rate_paise: rupeesToPaise(l.rate),
          })),
        }),
      });
      setMsg(`GRN ${res.grn_no} created — total ₹${(res.total_paise / 100).toFixed(2)}`);
      setLines([{ product_id: '', qty: '', rate: '' }]);
      await load();
    } catch (e) { setErr(/** @type {Error} */ (e).message); }
    finally { setBusy(false); }
  }

  async function submitCsv(/** @type {React.FormEvent} */ e) {
    e.preventDefault();
    setBusy(true); setErr(''); setMsg('');
    try {
      // CSV columns: sku, qty, rate  (header row optional).
      const parsed = csv
        .trim()
        .split('\n')
        .map((line) => line.split(',').map((c) => c.trim()))
        .filter((cols) => cols.length >= 3 && cols[0] && cols[0].toLowerCase() !== 'sku');

      const skuToId = /** @type {Record<string, string>} */ ({});
      for (const p of products) skuToId[p.sku] = p.id;

      const csvLines = parsed.map((cols) => {
        const id = skuToId[cols[0]];
        if (!id) throw new Error(`unknown SKU ${cols[0]}`);
        return {
          product_id: id,
          qty_purchased: Number(cols[1]),
          rate_paise: rupeesToPaise(cols[2]),
        };
      });
      if (csvLines.length === 0) throw new Error('no valid CSV rows');

      const res = await api(`/admin/branches/${branchId}/purchases`, {
        method: 'POST',
        body: JSON.stringify({
          vendor_id: header.vendor_id,
          mode: header.mode,
          seller_state: header.seller_state,
          vendor_state: header.vendor_state,
          invoice_no: header.invoice_no || undefined,
          lines: csvLines,
        }),
      });
      setMsg(`GRN ${res.grn_no} created — ${csvLines.length} lines — total ₹${(res.total_paise / 100).toFixed(2)}`);
      setCsv('');
      await load();
    } catch (e) { setErr(/** @type {Error} */ (e).message); }
    finally { setBusy(false); }
  }

  /** @type {Record<string, string>} */
  const vendorName = Object.fromEntries(vendors.map((v) => [v.id, v.name]));

  return (
    <div className="space-y-4">
      <div className="card space-y-3">
        <h2 className="font-semibold">GRN header</h2>
        <div className="grid grid-cols-5 gap-3">
          <SelectInput
            label="Vendor"
            value={header.vendor_id}
            onChange={(v) => setHeader({ ...header, vendor_id: v })}
            options={[{ value: '', label: 'pick…' }, ...vendors.map((v) => ({ value: v.id, label: v.name }))]}
          />
          <SelectInput
            label="Mode"
            value={header.mode}
            onChange={(v) => setHeader({ ...header, mode: v })}
            options={[{ value: 'MRP', label: 'MRP' }, { value: 'LOOSE', label: 'LOOSE' }, { value: 'DUOM', label: 'DUOM (weighed)' }]}
          />
          <TextInput label="Seller state" value={header.seller_state} onChange={(v) => setHeader({ ...header, seller_state: v })} />
          <TextInput label="Vendor state" value={header.vendor_state} onChange={(v) => setHeader({ ...header, vendor_state: v })} />
          <TextInput label="Invoice no" value={header.invoice_no} onChange={(v) => setHeader({ ...header, invoice_no: v })} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <form onSubmit={submitManual} className="card space-y-3">
          <h2 className="font-semibold">Manual lines</h2>
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-[2fr_1fr_1fr_auto] gap-2 items-end">
              <SelectInput
                label={i === 0 ? 'Product' : ''}
                value={l.product_id}
                onChange={(v) => setLines(lines.map((x, j) => (j === i ? { ...x, product_id: v } : x)))}
                options={[{ value: '', label: 'pick…' }, ...products.map((p) => ({ value: p.id, label: `${p.sku} — ${p.name}` }))]}
              />
              <TextInput label={i === 0 ? 'Qty' : ''} value={l.qty} onChange={(v) => setLines(lines.map((x, j) => (j === i ? { ...x, qty: v } : x)))} />
              <TextInput label={i === 0 ? 'Rate ₹' : ''} value={l.rate} onChange={(v) => setLines(lines.map((x, j) => (j === i ? { ...x, rate: v } : x)))} />
              <button type="button" className="btn-secondary px-2" onClick={() => setLines(lines.filter((_, j) => j !== i))}>×</button>
            </div>
          ))}
          <button type="button" className="btn-secondary text-sm" onClick={() => setLines([...lines, { product_id: '', qty: '', rate: '' }])}>+ add line</button>
          {err && <p className="text-red-400 text-sm">{err}</p>}
          {msg && <p className="text-green-400 text-sm">{msg}</p>}
          <button disabled={busy || !header.vendor_id} className="btn-primary w-full">{busy ? 'Saving…' : 'Create GRN'}</button>
        </form>

        <form onSubmit={submitCsv} className="card space-y-3">
          <h2 className="font-semibold">Bulk CSV upload</h2>
          <p className="text-sm text-neutral-400">Columns: <code>sku, qty, rate_rupees</code>. Header optional.</p>
          <textarea
            className="input w-full font-mono text-sm h-40"
            placeholder="HQ-001,10,135&#10;HQ-002,5,99.50"
            value={csv}
            onChange={(e) => setCsv(e.target.value)}
          />
          <button disabled={busy || !header.vendor_id} className="btn-primary w-full">{busy ? 'Saving…' : 'Ingest CSV'}</button>
        </form>
      </div>

      <div className="card">
        <h2 className="font-semibold mb-2">Recent GRNs ({rows.length})</h2>
        <table className="w-full text-sm">
          <thead className="text-neutral-400 text-left border-b border-neutral-800">
            <tr><th className="py-2">GRN</th><th>Date</th><th>Vendor</th><th>Mode</th><th>Total</th></tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p._id} className="border-b border-neutral-900">
                <td className="py-1 font-mono">{p.grn_no}</td>
                <td className="text-neutral-500">{fmtCell(p.grn_date)}</td>
                <td>{vendorName[p.vendor_id] ?? p.vendor_id}</td>
                <td>{p.mode}</td>
                <td>{fmtMoney(p.total_paise)}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="py-3 text-neutral-500">No purchases yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Reusable CRUD pattern for branch-scoped masters: create form on the left,
 * paginated list on the right. Fields drive both the form and the row keys
 * sent in the upsert payload.
 * @param {{ branchId: string, table: string, title: string, fields: { key: string, label: string, required?: boolean }[], columns: string[] }} props
 */
function CrudTab({ branchId, table, title, fields, columns }) {
  const [rows, setRows] = useState(/** @type {any[]} */ ([]));
  /** @type {Record<string, string>} */
  const initForm = Object.fromEntries(fields.map((f) => [f.key, '']));
  const [form, setForm] = useState(initForm);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  async function load() {
    const r = await api(`/admin/branches/${branchId}/data/${table}?limit=500`);
    setRows(r.rows);
  }
  useEffect(() => { load().catch((e) => setErr(e.message)); }, [branchId, table]);

  async function submit(/** @type {React.FormEvent} */ e) {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const doc = /** @type {Record<string, unknown>} */ ({});
      for (const f of fields) {
        const v = form[f.key];
        if (v !== '' && v != null) doc[f.key] = v;
      }
      await api(`/admin/branches/${branchId}/upsert`, {
        method: 'POST',
        body: JSON.stringify({ table, doc }),
      });
      setForm(initForm);
      await load();
    } catch (e) { setErr(/** @type {Error} */ (e).message); }
    finally { setBusy(false); }
  }

  return (
    <div className="grid grid-cols-3 gap-4">
      <form onSubmit={submit} className="card col-span-1 space-y-3 self-start">
        <h2 className="font-semibold">Add {title}</h2>
        {fields.map((f) => (
          <TextInput key={f.key} label={f.label} value={form[f.key]} onChange={(v) => setForm({ ...form, [f.key]: v })} required={f.required} />
        ))}
        {err && <p className="text-red-400 text-sm">{err}</p>}
        <button disabled={busy} className="btn-primary w-full">{busy ? 'Saving…' : 'Create'}</button>
      </form>
      <div className="card col-span-2">
        <h2 className="font-semibold mb-2">{title}s ({rows.length})</h2>
        <table className="w-full text-sm">
          <thead className="text-neutral-400 text-left border-b border-neutral-800">
            <tr>{columns.map((c) => <th key={c} className="py-2">{c}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r._id} className="border-b border-neutral-900">
                {columns.map((c) => <td key={c} className="py-1">{fmtCell(r[c])}</td>)}
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={columns.length} className="py-3 text-neutral-500">empty</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** @param {{ table: string, branchId: string, columns: string[] }} props */
function GenericList({ table, branchId, columns }) {
  const [rows, setRows] = useState(/** @type {any[]} */ ([]));
  const [err, setErr] = useState('');
  useEffect(() => {
    api(`/admin/branches/${branchId}/data/${table}?limit=200`)
      .then((r) => setRows(r.rows))
      .catch((e) => setErr(e.message));
  }, [table, branchId]);
  if (err) return <p className="text-red-400">{err}</p>;
  return (
    <div className="card">
      <h2 className="font-semibold mb-2">{table} ({rows.length})</h2>
      <table className="w-full text-sm">
        <thead className="text-neutral-400 text-left border-b border-neutral-800">
          <tr>{columns.map((c) => <th key={c} className="py-2">{c}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r._id} className="border-b border-neutral-900">
              {columns.map((c) => <td key={c} className="py-1">{fmtCell(r[c])}</td>)}
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={columns.length} className="py-3 text-neutral-500">empty</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

/** @param {{ label: string, value: string, onChange: (v: string) => void, required?: boolean }} props */
function TextInput({ label, value, onChange, required }) {
  return (
    <label className="block">
      <span className="text-sm text-neutral-400">{label}{required && ' *'}</span>
      <input className="input mt-1 w-full" value={value} onChange={(e) => onChange(e.target.value)} required={required} />
    </label>
  );
}

/** @param {{ label: string, value: string, onChange: (v: string) => void, options: { value: string, label: string }[] }} props */
function SelectInput({ label, value, onChange, options }) {
  return (
    <label className="block">
      <span className="text-sm text-neutral-400">{label}</span>
      <select className="input mt-1 w-full" value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

/** @param {string} v */
function rupeesToPaise(v) {
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}

/** @param {unknown} v */
function fmtMoney(v) {
  if (v == null) return '—';
  if (typeof v === 'object') {
    const s = /** @type {any} */ (v).$numberDecimal ?? /** @type {any} */ (v).toString?.();
    return `₹${s}`;
  }
  if (typeof v === 'number') return `₹${(v / 100).toFixed(2)}`;
  return String(v);
}

/** @param {unknown} v */
function fmtCell(v) {
  if (v == null) return '—';
  if (typeof v === 'object') {
    const s = /** @type {any} */ (v).$numberDecimal;
    if (s) return s;
    if (v instanceof Date) return v.toISOString();
    return JSON.stringify(v);
  }
  return String(v);
}
